import axiosInstance from './BaseService';

export interface CommunicationRecipient {
  email?: string;
  phone?: string;
}

export interface CommunicationVariables {
  [key: string]: any;
}

export interface CommunicationLinks {
  email?: {
    link: string;
    subject: string;
    body: string;
  };
  sms?: {
    link: string;
    body: string;
  };
  whatsapp?: {
    link: string;
    body: string;
  };
}

/**
 * Frontend Communication Service
 * Handles all link-based communications using backend templates
 */
class CommunicationService {
  
  /**
   * Get device communication links from backend
   */
  static async getDeviceLinks(
    templateType: string,
    recipient: CommunicationRecipient,
    variables: CommunicationVariables = {}
  ): Promise<CommunicationLinks> {
    try {
      const response = await axiosInstance.post('/communication/device-links', {
        recipient,
        templateType,
        variables
      });
      
      return response.data.links;
    } catch (error) {
      console.error('Error getting device links:', error);
      throw new Error('Failed to generate communication links');
    }
  }

  /**
   * Open communication link in new window/tab
   */
  static openLink(link: string): void {
    if (link.startsWith('mailto:') || link.startsWith('sms:') || link.startsWith('tel:')) {
      window.location.href = link;
    } else if (link.startsWith('https://wa.me/')) {
      window.open(link, '_blank');
    } else {
      window.open(link, '_blank');
    }
  }

  /**
   * Contact support with issue details
   */
  static async contactSupport(
    userName: string,
    userPhone: string,
    issueType: string,
    issueDescription: string = '',
    channel: 'email' | 'sms' | 'whatsapp' = 'whatsapp'
  ): Promise<void> {
    const supportPhone = process.env.REACT_APP_SUPPORT_PHONE || '999999999';
    const supportEmail = process.env.REACT_APP_SUPPORT_EMAIL || 'natakitadotcom@gmail.com';
    
    const recipient = {
      phone: supportPhone,
      email: supportEmail
    };
    
    const variables = {
      userName,
      userPhone,
      issueType,
      issueDescription
    };
    
    const links = await this.getDeviceLinks('CONTACT_SUPPORT', recipient, variables);
    
    if (links[channel]) {
      this.openLink(links[channel]!.link);
    }
  }

  /**
   * Share event details
   */
  static async shareEvent(
    eventName: string,
    eventDate: string,
    eventTime: string,
    venue: string,
    bookingUrl: string,
    recipientPhone?: string,
    channel: 'email' | 'sms' | 'whatsapp' = 'whatsapp'
  ): Promise<void> {
    try {
      const recipient = {
        phone: recipientPhone || '',
        email: '' // Will be filled by user's email client
      };
      
      const variables = {
        eventName,
        eventDate,
        eventTime,
        venue,
        bookingUrl
      };
      
      console.log('Sharing event with variables:', variables);
      
      const links = await this.getDeviceLinks('EVENT_SHARING', recipient, variables);
      
      console.log('Generated links:', links);
      
      if (links[channel]) {
        console.log('Opening link:', links[channel]!.link);
        this.openLink(links[channel]!.link);
      } else {
        console.error(`No link generated for channel: ${channel}`);
      }
    } catch (error) {
      console.error('Error in shareEvent:', error);
      throw error;
    }
  }

  /**
   * Share program details
   */
  static async shareProgram(
    programTitle: string,
    groupName: string,
    programGenre: string,
    programDuration: string,
    programDescription: string,
    programUrl: string,
    channel: 'email' | 'sms' | 'whatsapp' = 'whatsapp'
  ): Promise<void> {
    const recipient = {
      phone: '',
      email: ''
    };
    
    const variables = {
      programTitle,
      groupName,
      programGenre,
      programDuration,
      programDescription,
      programUrl
    };
    
    const links = await this.getDeviceLinks('PROGRAM_SHARING', recipient, variables);
    
    if (links[channel]) {
      this.openLink(links[channel]!.link);
    }
  }

  /**
   * Share group details
   */
  static async shareGroup(
    groupName: string,
    groupLocation: string,
    groupEstablished: string,
    groupDescription: string,
    groupUrl: string,
    channel: 'email' | 'sms' | 'whatsapp' = 'whatsapp'
  ): Promise<void> {
    try {
      const recipient = {
        phone: '',
        email: ''
      };
      
      const variables = {
        groupName,
        groupLocation,
        groupEstablished,
        groupDescription,
        groupUrl
      };
      
      const links = await this.getDeviceLinks('GROUP_SHARING', recipient, variables);
      
      if (links[channel]) {
        this.openLink(links[channel]!.link);
      }
    } catch (error) {
      console.error('Error in shareGroup:', error);
      throw error;
    }
  }

  /**
   * Contact organizer about event
   */
  static async contactOrganizer(
    organizerName: string,
    organizerPhone: string,
    eventName: string,
    eventDate: string,
    eventTime: string,
    venue: string,
    customerName: string,
    customerPhone: string,
    customerEmail: string,
    channel: 'email' | 'sms' | 'whatsapp' = 'whatsapp',
    organizerEmail: string = ''
  ): Promise<void> {
    try {
      console.log('Contacting organizer with data:', {
        organizerName, organizerPhone, eventName, eventDate, eventTime, venue,
        customerName, customerPhone, customerEmail, channel
      });
      
      // Format phone number with country code
      let formattedOrganizerPhone = organizerPhone ? organizerPhone.replace(/[^0-9]/g, '') : '';
      if (formattedOrganizerPhone && !formattedOrganizerPhone.startsWith('91') && formattedOrganizerPhone.length === 10) {
        formattedOrganizerPhone = '91' + formattedOrganizerPhone;
      }
      
      const response = await axiosInstance.post('/communication/contact-organizer', {
        organizerName,
        organizerPhone: formattedOrganizerPhone,
        organizerEmail: organizerEmail,
        eventName,
        eventDate,
        eventTime,
        venue,
        customerName,
        customerPhone,
        customerEmail
      });
      
      const { links } = response.data;
      console.log('Generated organizer contact links:', links);
      
      if (links[channel]) {
        this.openLink(links[channel].link);
      } else {
        console.error(`No link generated for channel: ${channel}`);
      }
    } catch (error) {
      console.error('Error contacting organizer:', error);
      throw error;
    }
  }

  /**
   * Send feedback about event
   */
  static async sendFeedback(
    customerName: string,
    eventName: string,
    bookingCode: string,
    eventDate: string,
    channel: 'email' | 'sms' | 'whatsapp' = 'email'
  ): Promise<void> {
    const feedbackPhone = process.env.REACT_APP_FEEDBACK_PHONE || '999999999';
    const feedbackEmail = process.env.REACT_APP_FEEDBACK_EMAIL || 'natakitadotcom@gmail.com';
    
    const recipient = {
      phone: feedbackPhone,
      email: feedbackEmail
    };
    
    const variables = {
      customerName,
      eventName,
      bookingCode,
      eventDate
    };
    
    const links = await this.getDeviceLinks('FEEDBACK_REQUEST', recipient, variables);
    
    if (links[channel]) {
      this.openLink(links[channel]!.link);
    }
  }

  /**
   * Share booking details
   */
  static async shareBooking(
    bookingCode: string,
    eventName: string,
    eventDate: string,
    eventTime: string,
    venue: string,
    seatNumbers: string,
    amount: number,
    customerName: string,
    channel: 'email' | 'sms' | 'whatsapp' = 'whatsapp'
  ): Promise<void> {
    const recipient = {
      phone: '',
      email: ''
    };
    
    const variables = {
      bookingCode,
      eventName,
      eventDate,
      eventTime,
      venue,
      seatNumbers,
      amount,
      customerName
    };
    
    const links = await this.getDeviceLinks('BOOKING_SHARE', recipient, variables);
    
    if (links[channel]) {
      this.openLink(links[channel]!.link);
    }
  }

  /**
   * Invite to group
   */
  static async inviteToGroup(
    groupName: string,
    groupDescription: string,
    groupLocation: string,
    groupUrl: string,
    inviterName: string,
    recipientPhone?: string,
    channel: 'email' | 'sms' | 'whatsapp' = 'whatsapp'
  ): Promise<void> {
    const recipient = {
      phone: recipientPhone || '',
      email: ''
    };
    
    const variables = {
      groupName,
      groupDescription,
      groupLocation,
      groupUrl,
      inviterName
    };
    
    const links = await this.getDeviceLinks('GROUP_INVITATION', recipient, variables);
    
    if (links[channel]) {
      this.openLink(links[channel]!.link);
    }
  }

  /**
   * Emergency contact
   */
  static async emergencyContact(
    userName: string,
    userPhone: string,
    issueType: string,
    issueDescription: string,
    bookingCode?: string,
    eventName?: string,
    eventDate?: string,
    channel: 'email' | 'sms' | 'whatsapp' = 'whatsapp'
  ): Promise<void> {
    const emergencyPhone = process.env.REACT_APP_EMERGENCY_PHONE || '999999999';
    const emergencyEmail = process.env.REACT_APP_EMERGENCY_EMAIL || 'natakitadotcom@gmail.com';
    
    const recipient = {
      phone: emergencyPhone,
      email: emergencyEmail
    };
    
    const variables = {
      userName,
      userPhone,
      issueType,
      issueDescription,
      bookingCode: bookingCode || 'N/A',
      eventName: eventName || 'N/A',
      eventDate: eventDate || 'N/A'
    };
    
    const links = await this.getDeviceLinks('EMERGENCY_CONTACT', recipient, variables);
    
    if (links[channel]) {
      this.openLink(links[channel]!.link);
    }
  }

  /**
   * Contact company
   */
  static async contactCompany(
    userName: string,
    userPhone: string,
    userEmail: string,
    inquiryType: string,
    message: string,
    channel: 'email' | 'sms' | 'whatsapp' = 'email'
  ): Promise<void> {
    const companyPhone = process.env.REACT_APP_COMPANY_PHONE || '999999999';
    const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'natakitadotcom@gmail.com';
    
    const recipient = {
      phone: companyPhone,
      email: companyEmail
    };
    
    const variables = {
      userName,
      userPhone,
      userEmail,
      inquiryType,
      message
    };
    
    const links = await this.getDeviceLinks('CONTACT_COMPANY', recipient, variables);
    
    if (links[channel]) {
      this.openLink(links[channel]!.link);
    }
  }
}

export default CommunicationService;